import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatCurrency, formatDay } from '~/lib/format';
import { distributions } from '~data';
import { clearHover, hoverAt, setHoverAt } from '~/stores/crosshair-store';

// The rate per unit, payment by payment, oldest to newest.
//
// The one view that makes a slow decline visible. A table of sixty near-identical
// amounts does not: UMAX went from $0.1750 to $0.1500 across twenty-two changes
// and no single row of that table looks like anything at all.
//
// A step line, not a smooth one, because a distribution rate is a step function.
// It holds flat until it is changed and then jumps. Joining the declarations with
// diagonals would draw a gradual drift that never happened — the fund did not pay
// $0.1631 on the way down, it paid $0.1750 until the month it paid $0.1500.
//
// Plotted against payment number rather than against time. The payments are the
// events, they are evenly spaced by design for a monthly payer, and spacing them
// by date would leave a fund that switched schedules with a compressed tail that
// reads as a change in the rate.
const VB_W = 100;
const VB_H = 40;
const PAD = 3;
const PAD_X = 3;

/** The payment record, for a caller with a header to put it in. Exported rather
    than drawn here: a cell title bar is a better place for a constant than the
    plot is, and it is the one line of chrome the chart already has. */
export const distributionSummary = (symbol: string): string | null => {
    // Sorted, because a cut is only a cut against the payment before it.
    const paid = [...distributions(symbol)]
        .map((d) => ({ t: d.payDate ?? d.exDate, amount: d.amount }))
        .sort((a, b) => a.t.localeCompare(b.t))
        .map((d) => d.amount);
    if (paid.length === 0) return null;
    let cuts = 0;
    let raises = 0;
    for (let i = 1; i < paid.length; i += 1) {
        if (paid[i] < paid[i - 1]) cuts += 1;
        else if (paid[i] > paid[i - 1]) raises += 1;
    }
    return `${paid.length} payments · ${cuts} cuts · ${raises} raises`;
};

export const DistributionChart: Component<{ symbol: string; height?: string }> = (props) => {
    /** Oldest first — the direction a decline is read in. */
    const payments = createMemo(() =>
        [...distributions(props.symbol)]
            .map((d) => ({ date: d.payDate ?? d.exDate, amount: d.amount }))
            .sort((a, b) => a.date.localeCompare(b.date)),
    );

    const span = createMemo(() => {
        const rates = payments().map((p) => p.amount);
        if (rates.length < 2) return null;
        const lo = Math.min(...rates);
        const hi = Math.max(...rates);
        return { lo, hi, vSpan: hi - lo || Math.abs(hi) || 1 };
    });

    const x = (i: number): number => (payments().length < 2 ? VB_W / 2 : PAD_X + (i / (payments().length - 1)) * (VB_W - PAD_X * 2));
    const y = (v: number): number => VB_H - PAD - ((v - span()!.lo) / span()!.vSpan) * (VB_H - PAD * 2);

    const step = createMemo(() =>
        payments()
            .map((p, i) => (i === 0 ? `M${x(0).toFixed(3)} ${y(p.amount).toFixed(3)}` : `L${x(i).toFixed(3)} ${y(payments()[i - 1].amount).toFixed(3)} L${x(i).toFixed(3)} ${y(p.amount).toFixed(3)}`))
            .join(' '),
    );

    /** Every point where the rate actually changed — the events the chart is
        about, marked so a single step in a long flat line is not missed. */
    const changes = createMemo(() => payments().map((p, i) => ({ ...p, i })).filter((p, i) => i > 0 && p.amount !== payments()[i - 1].amount));

    const [over, setOver] = createSignal(false);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });

    /**
     * The shared date, snapped to a payment.
     *
     * This chart's x is payment number, not time, so a date has to be resolved
     * to the last payment made on or before it. That makes the crosshair step
     * between payments while the price chart's slides — which is honest: between
     * two payments there is no third rate to point at.
     */
    const hover = (): number | null => {
        const t = hoverAt();
        const paid = payments();
        if (t == null || paid.length === 0) return null;
        if (t < Date.parse(paid[0].date)) return null;
        let found = 0;
        for (let i = 0; i < paid.length; i += 1) {
            if (Date.parse(paid[i].date) > t) break;
            found = i;
        }
        return found;
    };
    const at = () => (hover() == null ? null : (payments()[hover()!] ?? null));

    /** The payment before the hovered one, so the tooltip can say what changed
        rather than only what the rate is. */
    const prior = () => (hover() == null || hover()! < 1 ? null : (payments()[hover()! - 1] ?? null));

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const count = payments().length;
        if (rect.width <= 0 || count === 0) return;
        // Inverts x() rather than mapping across the whole box — the plot is
        // inset by PAD_X at each end. Same note as close-chart.
        const across = ((e.clientX - rect.left) / rect.width) * VB_W;
        const fraction = Math.min(1, Math.max(0, (across - PAD_X) / (VB_W - PAD_X * 2)));
        // Published as a date, since that is the only thing the other chart on
        // the page shares with this one.
        const at = payments()[Math.round(fraction * (count - 1))];
        if (at) setHoverAt(Date.parse(at.date));
        setCursor({ x: e.clientX, y: e.clientY });
        setOver(true);
    };

    const first = () => payments()[0] ?? null;

    return (
        <Show
            when={span()}
            fallback={
                <div class='flex min-h-0 flex-1 items-center justify-center'>
                    <Text tone='muted'>{payments().length === 1 ? 'Only one distribution paid so far' : 'No distributions paid'}</Text>
                </div>
            }>
            <div class={`relative w-full ${props.height ?? 'h-40'}`} onMouseMove={track} onMouseLeave={() => { setOver(false); clearHover(); }}>
                {/* crispEdges, which is safe here and nowhere else in the app.

                    A step function is axis-aligned by construction — every
                    segment is horizontal or vertical — and the viewBox is
                    stretched to fit, so those segments land on fractional device
                    pixels and a 2px stroke gets antialiased across three rows of
                    them. That reads as blur. Turning antialiasing off snaps each
                    segment to the pixel grid and the line comes out sharp.

                    The price charts must NOT do this: their lines are diagonal,
                    and without antialiasing a diagonal is a staircase. */}
                <svg class='h-full w-full' viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio='none' fill='none' shape-rendering='crispEdges'>
                    {/* The opening rate, so a decline reads as a decline rather
                        than as a line that happens to slope. Without it the eye
                        has nothing fixed to measure against. */}
                    <line x1={PAD_X} y1={y(first()!.amount)} x2={VB_W - PAD_X} y2={y(first()!.amount)} class='stroke-muted-foreground' stroke-width='1' stroke-dasharray='3 3' stroke-opacity='0.6' vector-effect='non-scaling-stroke' />
                    <path d={step()} class='stroke-foreground' stroke-width='2' stroke-linejoin='miter' vector-effect='non-scaling-stroke' />
                    <Show when={hover() != null}>
                        <line x1={x(hover()!)} y1='0' x2={x(hover()!)} y2={VB_H} class='stroke-muted-foreground' stroke-width='1' vector-effect='non-scaling-stroke' />
                    </Show>
                </svg>

                {/* Cuts and raises marked as dots. An HTML element rather than an
                    SVG circle: the viewBox is stretched to fit and a circle in it
                    would render as an ellipse. */}
                <For each={changes()}>
                    {(c) => (
                        <span
                            class={`pointer-events-none absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${c.amount < payments()[c.i - 1].amount ? 'bg-red' : 'bg-green'}`}
                            style={{ left: `${(x(c.i) / VB_W) * 100}%`, top: `${(y(c.amount) / VB_H) * 100}%` }}
                        />
                    )}
                </For>

            </div>

            {/* Instant, and at the pointer. `title` waits about a second, which
                on a chart where the whole plot is the hit area reads as nothing
                having happened. Portalled and fixed for the same reason as the
                treemap's: Panel is overflow-hidden and would clip it. */}
            <Show when={over() && at()}>
                <Portal>
                    <div
                        class='pointer-events-none fixed z-[200] text-foreground rounded border bg-background px-2 py-1 shadow'
                        style={{ left: `${Math.min(cursor().x + 12, window.innerWidth - 220)}px`, top: `${Math.min(cursor().y + 12, window.innerHeight - 64)}px` }}>
                        <div class='flex items-baseline gap-2 whitespace-nowrap'>
                            <Text tone='muted'>{formatDay(at()!.date)}</Text>
                            <Text weight='medium'>{formatCurrency(at()!.amount, 4)}</Text>
                        </div>
                        {/* Only where the rate actually moved. On a flat stretch
                            there is nothing to say and a "held at" line on every
                            one of a hundred payments is noise. */}
                        <Show when={prior() && prior()!.amount !== at()!.amount}>
                            <Text tone={at()!.amount < prior()!.amount ? 'red' : 'green'} class='block whitespace-nowrap'>
                                {at()!.amount < prior()!.amount ? 'cut' : 'raised'} from {formatCurrency(prior()!.amount, 4)}
                            </Text>
                        </Show>
                    </div>
                </Portal>
            </Show>
        </Show>
    );
};

export default DistributionChart;
